import React from "react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MATRIX_HERO_IMG } from "@/constants/engines";
import { INTAKE_URL } from "@/constants/links";
import WearableDataPanel from "@/components/WearableDataPanel";
import EngineGrid from "@/components/EngineGrid";

const PLAN_OUTPUTS = [
  "Food, training, and recovery built around your real data.",
  "Smart habits that fit your week. Not someone else's.",
  "Updated as your data updates — never stale.",
];

function MatrixHeader() {
  return (
    <div className="mb-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
      <div className="lg:col-span-7">
        <div className="iw-overline mb-3">§ 04 — Patent Pending · 7 Engines</div>
        <h2 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl">
          The Integrated Matrix Engine —{" "}
          <span className="text-[color:var(--iw-blue)]">Patent-Pending Technology</span>{" "}
          That Turns Wearable Data Into Your Wellness Plan.
        </h2>
      </div>
      <div className="lg:col-span-5">
        <p className="text-xl leading-relaxed text-[color:var(--iw-body)]">
          The first of <strong className="font-semibold text-[color:var(--iw-primary)]">seven patent-pending engines</strong> that turn your wearable data into <strong className="font-semibold text-[color:var(--iw-primary)]">one medically supervised, personalized optimization plan</strong>.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 border border-[color:var(--iw-blue)]/60 bg-[color:var(--iw-blue)]/5 px-3 py-1.5 font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-blue)]">
          <span className="iw-pulse-dot block h-1.5 w-1.5 rounded-full bg-[color:var(--iw-blue)]" />
          Patent Pending · 7 Engines
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[color:var(--iw-muted-foreground)]">
          Our engines turn your wearable and optional test data into <strong className="text-[color:var(--iw-primary)]">wellness estimates</strong> and personalized plans. They support your goals — they don't diagnose, treat, or replace medical care.
        </p>
      </div>
    </div>
  );
}

function MatrixHeroImage() {
  return (
    <div className="iw-scan-sweep relative overflow-hidden border border-[color:var(--iw-border)]">
      <img
        src={MATRIX_HERO_IMG}
        alt="Integrated Matrix Engine — turns your wearable data into a real plan"
        className="block h-[320px] w-full object-cover sm:h-[420px] lg:h-[480px]"
        loading="lazy"
        decoding="async"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#11296B]/55 via-[#11296B]/10 to-transparent" />
      <span className="pointer-events-none absolute left-4 top-4 h-4 w-4 border-l-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute right-4 top-4 h-4 w-4 border-r-2 border-t-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute left-4 bottom-4 h-4 w-4 border-l-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />
      <span className="pointer-events-none absolute right-4 bottom-4 h-4 w-4 border-r-2 border-b-2 border-[color:var(--iw-blue-bright)]" aria-hidden />

      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
        <div className="border-l-2 border-[color:var(--iw-accent-red)] pl-4">
          <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-blue-bright)]">
            Wearable data in · 1 real plan out
          </div>
          <div className="mt-1 font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
            Your data. <span className="text-[color:var(--iw-accent-red)]">Working for you.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanOutputBlock() {
  return (
    <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-10">
      <div>
        <div className="iw-overline mb-4">What comes out · Your plan</div>
        <h3 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-3xl">
          A real plan you can actually <span className="text-[color:var(--iw-blue)]">follow</span>.
        </h3>
      </div>
      <div className="border border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] p-6">
        <ul className="space-y-3">
          {PLAN_OUTPUTS.map((o) => (
            <li key={o} className="flex items-start gap-3">
              <ArrowRight className="mt-1 h-4 w-4 flex-shrink-0 text-[color:var(--iw-accent-red)]" />
              <span className="text-base leading-relaxed text-[color:var(--iw-primary)]">{o}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function MatrixFooterCta() {
  return (
    <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-[color:var(--iw-border)] pt-8 sm:flex-row sm:items-center">
      <p className="max-w-2xl text-lg text-[color:var(--iw-body)]">
        Cutting-edge AI does the heavy lifting. You get one plan you can follow.
      </p>
      <a
        href={INTAKE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="iw-btn-red inline-flex flex-shrink-0 items-center gap-2 px-6 py-3 font-mono text-[13px] uppercase tracking-[0.22em]"
      >
        Get Started <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function MatrixEngineSection() {
  return (
    <section
      id="matrix-engine"
      className="relative overflow-hidden border-b border-[color:var(--iw-border)] bg-white py-20 sm:py-28"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-50" aria-hidden />
      <div
        className="pointer-events-none absolute -top-32 right-1/4 h-[520px] w-[520px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, rgba(30, 58, 138, 0.30), transparent 70%)" }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <MatrixHeader />
        <MatrixHeroImage />
        <WearableDataPanel />
        <PlanOutputBlock />
        <EngineGrid />
        <MatrixFooterCta />
      </div>
    </section>
  );
}
